import {
  forbiddenCharacters,
  isEmpty,
  isRichTextEditorEmpty,
  validateLength,
} from '../../../utility/validation';
import { FIELD_TYPES } from '../../../constants/fieldTypes';
import {
  CANDIDATE_ORDERING,
  CANDIDATE_ORDERING_DEFAULT,
} from '../../../constants/job';
import { PARAMS } from '../../../constants/pages';
import { CANDIDATE_NAVIGATIONS } from '../../shared/candidateInfo/config';

export const QUALIFIED_STATUS = {
  QUALIFIED: 'qualified',
  DISQUALIFIED: 'disqualified',
};

export const prepareCandidateOrderingOptions = translations => [
  {
    id: CANDIDATE_ORDERING.NAME_ASC,
    name: translations.orderingOptions.nameAsc,
  },
  {
    id: CANDIDATE_ORDERING.NAME_DESC,
    name: translations.orderingOptions.nameDesc,
  },
  {
    id: CANDIDATE_ORDERING.LAST_UPDATE_DESC,
    name: translations.orderingOptions.updatedAtNewest,
  },
  {
    id: CANDIDATE_ORDERING.LAST_UPDATE_ASC,
    name: translations.orderingOptions.updatedAtOldest,
  },
  {
    id: CANDIDATE_ORDERING.ADDED_TO_JOB_DESC,
    name: translations.orderingOptions.addedToJobNewest,
  },
  {
    id: CANDIDATE_ORDERING.ADDED_TO_JOB_ASC,
    name: translations.orderingOptions.addedToJobOldest,
  },
];

export const prepareValidCandidateOrdering = ordering =>
  Object.values(CANDIDATE_ORDERING).includes(ordering)
    ? ordering
    : CANDIDATE_ORDERING_DEFAULT;

export const prepareJobDetailsQueryParams = ({
  search,
  isQualifiedView,
  ordering,
} = {}) => ({
  ...(search ? { [PARAMS.SEARCH]: search } : {}),
  ...(ordering ? { [PARAMS.ORDERING]: ordering } : {}),
  [PARAMS.STATUS]: isQualifiedView
    ? QUALIFIED_STATUS.DISQUALIFIED
    : QUALIFIED_STATUS.QUALIFIED,
});

export const getSwitchItems = (labels, candidates, hasCandidates) => {
  const disqualifiedUsersCount = hasCandidates
    ? candidates?.filter(candidate => candidate.is_disqualified).length
    : 0;
  const qualifiedUsersCount = hasCandidates
    ? candidates?.filter(candidate => !candidate.is_disqualified).length
    : 0;

  return [
    {
      value: false,
      label: qualifiedUsersCount
        ? `${labels.qualified} (${qualifiedUsersCount})`
        : labels.qualified,
    },
    {
      value: true,
      label: disqualifiedUsersCount
        ? `${labels.disqualified} (${disqualifiedUsersCount})`
        : labels.disqualified,
    },
  ];
};

export const getNavItems = job => {
  const totalNumberOfCandidates = job.steps?.reduce((acc, curr) => {
    return acc + curr.number_of_candidates;
  }, 0);

  return [
    {
      url: `/jobs/${job.id}/details/0/${CANDIDATE_NAVIGATIONS.PROFILE}/`,
      key: 'All',
      name: totalNumberOfCandidates
        ? `All (${totalNumberOfCandidates})`
        : `All`,
      end: false,
      isCapitalized: true,
    },
    ...(job.steps?.map(step => ({
      url: `/jobs/${job.id}/details/${step.id}/${CANDIDATE_NAVIGATIONS.PROFILE}/`,
      key: `${step.title}_${step.id}`,
      name: step.number_of_candidates
        ? `${step.title} (${step.number_of_candidates})`
        : `${step.title}`,
      end: false,
      isCapitalized: true,
    })) || []),
  ];
};

export const getCandidateNavigationItems = (jobId, stepId, query) => [
  {
    url: `/jobs/${jobId}/details/${stepId}/profile/?${query}`,
    key: 'Profile',
    name: 'Profile',
    end: false,
    isCapitalized: true,
  },
  {
    url: `/jobs/${jobId}/details/${stepId}/timeline/?${query}`,
    name: 'Timeline',
    key: 'Timeline',
    end: false,
    isCapitalized: true,
  },
  {
    url: `/jobs/${jobId}/details/${stepId}/communication/?${query}`,
    name: 'Communication',
    key: 'Communication',
    end: false,
    isCapitalized: true,
  },
  {
    url: `/jobs/${jobId}/details/${stepId}/reviews/?${query}`,
    name: 'Reviews',
    key: 'Reviews',
    end: false,
    isCapitalized: true,
  },
  {
    url: `/jobs/${jobId}/details/${stepId}/comments/?${query}`,
    name: 'Comments',
    key: 'Comments',
    end: false,
    isCapitalized: true,
  },
];

export const getVoteFields = () => [
  {
    name: 'comment',
    type: FIELD_TYPES.RICH_TEXT_EDITOR,
    required: true,
    translationKey: 'comment',
    validators: [
      {
        validator: value => validateLength(value, 0, 10000),
        type: 'maxLength',
      },
      {
        validator: value => isRichTextEditorEmpty(value),
        type: 'required',
      },
    ],
  },
  {
    name: 'vote',
    type: FIELD_TYPES.VOTE,
    required: true,
    translationKey: 'vote',
    validators: [
      {
        validator: isEmpty,
        type: 'required',
      },
    ],
  },
];

export const getVoteInitialData = vote => ({
  id: vote.id || undefined,
  vote: vote.vote || null,
  comment: vote.comment || '',
  reviewer: vote.reviewer || {},
});

export const GET_SEND_EMAIL_FIELDS = [
  {
    name: 'subject',
    type: FIELD_TYPES.TEXT,
    required: true,
    translationKey: 'subject',
    validators: [
      {
        validator: isEmpty,
        type: 'required',
      },
      {
        validator: forbiddenCharacters,
        type: 'forbiddenCharacters',
      },
      {
        validator: fieldValue => validateLength(fieldValue, 0, 255),
        type: 'maxLength',
      },
    ],
  },
  {
    name: 'body',
    type: FIELD_TYPES.TEXT,
    required: true,
    translationKey: 'body',
    multiline: true,
    rows: 10,
    validators: [
      {
        validator: isEmpty,
        type: 'required',
      },
      {
        validator: forbiddenCharacters,
        type: 'forbiddenCharacters',
      },
      {
        validator: fieldValue => validateLength(fieldValue, 0, 2000),
        type: 'messageMaxLength',
      },
    ],
  },
];

export const getSendEmailInitialData = () => ({
  subject: '',
  body: '',
});

export const getAddCommentFields = classes => {
  return [
    {
      name: 'content',
      type: FIELD_TYPES.RICH_TEXT_EDITOR,
      required: true,
      fieldWrapperClass: classes.content,
      privateNoteErrorClass: classes.commentErrorClass,
      translationKey: 'content',
      validators: [
        {
          validator: value => isRichTextEditorEmpty(value),
          type: 'required',
        },
        {
          validator: fieldValue => validateLength(fieldValue, 0, 10000),
          type: 'messageMaxLength',
        },
      ],
    },
  ];
};

export const getAddCommentInitialData = comment => ({
  id: comment.id || undefined,
  content: comment.content || '',
  author: comment.author || {},
});

export const getReviews = (reviews, hiringTeam) => {
  const results = reviews.reduce((acc, curr) => {
    const users = acc[curr.selection_step.order]
      ? { ...acc[curr.selection_step.order].reviews, [curr.id]: curr }
      : { [curr.id]: curr };

    return {
      ...acc,
      [curr.selection_step.order]: {
        title: curr.selection_step.title,
        stepId: curr.selection_step.id,
        reviews: users,
      },
    };
  }, {});

  return Object.entries(results).reduce((acc, [key, value]) => {
    return {
      ...acc,
      [key]: {
        ...value,
        pendingUsers: hiringTeam.filter(
          hiringTeamUser =>
            !Object.values(value.reviews).find(
              user => user.reviewer.id === hiringTeamUser.id
            )
        ),
      },
    };
  }, {});
};
